import { type RefObject, useLayoutEffect, useRef } from "react";

/** Load the next page once the user is in the last 20% of the scroll range. */
export const INFINITE_SCROLL_END_RATIO = 0.8;

const SCROLL_LISTENER_OPTIONS: AddEventListenerOptions = { passive: true };

const isOverflowScrollable = (value: string): boolean => value === "auto" || value === "scroll" || value === "overlay";

export const isScrollPastEndRatio = (root: Element, ratio = INFINITE_SCROLL_END_RATIO): boolean => {
	const viewport = root.clientHeight;
	if (viewport <= 0) {
		return false;
	}
	const scrollable = root.scrollHeight - viewport;
	if (scrollable <= 0) {
		return true;
	}
	return root.scrollTop / scrollable >= ratio;
};

const findScrollRoot = (sentinel: Element): Element => {
	let current = sentinel.parentElement;
	while (current) {
		if (isOverflowScrollable(getComputedStyle(current).overflowY)) {
			return current;
		}
		current = current.parentElement;
	}
	return document.scrollingElement ?? document.documentElement;
};

/**
 * Load more when the scroll root is at least 80% through its range.
 * Uses a passive scroll listener. Re-subscribes when `enabled` flips
 * so a still-near-end viewport can fetch the next page after items append.
 */
export const useInfiniteScrollSentinel = ({
	enabled,
	onIntersect,
	rootRef,
	sentinelRef,
}: {
	readonly enabled: boolean;
	readonly onIntersect: () => void;
	readonly rootRef?: RefObject<Element | null>;
	readonly sentinelRef?: RefObject<Element | null>;
}): void => {
	const onIntersectRef = useRef(onIntersect);
	onIntersectRef.current = onIntersect;

	useLayoutEffect(() => {
		if (!enabled) {
			return;
		}
		const root = rootRef?.current ?? (sentinelRef?.current ? findScrollRoot(sentinelRef.current) : null);
		if (!root) {
			return;
		}

		const maybeLoad = (): void => {
			if (isScrollPastEndRatio(root)) {
				onIntersectRef.current();
			}
		};

		const frame = window.requestAnimationFrame(maybeLoad);
		root.addEventListener("scroll", maybeLoad, SCROLL_LISTENER_OPTIONS);
		const resizeObserver = new ResizeObserver(maybeLoad);
		resizeObserver.observe(root);
		const content = root.firstElementChild;
		if (content) {
			resizeObserver.observe(content);
		}

		return () => {
			window.cancelAnimationFrame(frame);
			root.removeEventListener("scroll", maybeLoad);
			resizeObserver.disconnect();
		};
	}, [enabled, rootRef, sentinelRef]);
};
